import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    MdPhoneAndroid,
    MdVisibility,
    MdVisibilityOff,
    MdContentCopy,
    MdCheckCircle,
} from 'react-icons/md';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import logoLocaSmartCheck from '../../assets/logo-locasmartcheck.png';

interface ModalOnboardingProps {
    open: boolean;
    onClose: () => void;
    email: string;
    senha: string;
}

function ModalOnboarding({ open, onClose, email, senha }: ModalOnboardingProps) {
    const [senhaVisivel, setSenhaVisivel] = useState(false);
    const [copiado, setCopiado] = useState<string | null>(null);

    const copiar = (texto: string, campo: string) => {
        navigator.clipboard.writeText(texto);
        setCopiado(campo);
        setTimeout(() => setCopiado(null), 2000);
    };

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
            }}
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #0a1929 0%, #132f4c 100%)',
                color: 'white',
                p: 3,
                textAlign: 'center',
            }}>
                <MdCheckCircle style={{ fontSize: 48, color: '#66bb6a', marginBottom: 8 }} />
                <Typography variant="h5" fontWeight="800">
                    LocaSmartCheck Ativado!
                </Typography>
                <Typography variant="body2" sx={{ opacity: 1, mt: 0.5, color: '#e0e0e0' }}>
                    Agora baixe o app e faça login para realizar vistorias
                </Typography>
            </Box>

            <DialogContent sx={{ p: 3 }}>
                {/* Passo 1 - Baixar o app */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            backgroundColor: '#1976d2', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, fontWeight: 'bold',
                        }}>1</Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Baixe o aplicativo
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, pl: 4.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<FaGooglePlay />}
                            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.locasmartcheck.app', '_blank')}
                            sx={{
                                flex: 1,
                                backgroundColor: '#414141',
                                '&:hover': { backgroundColor: '#2d2d2d' },
                                textTransform: 'none',
                                py: 1.2,
                            }}
                        >
                            Google Play
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<FaApple />}
                            onClick={() => window.open('https://apps.apple.com/br/app/locasmartcheck/id6760604574', '_blank')}
                            sx={{
                                flex: 1,
                                backgroundColor: '#000',
                                '&:hover': { backgroundColor: '#333' },
                                textTransform: 'none',
                                py: 1.2,
                            }}
                        >
                            App Store
                        </Button>
                    </Box>
                </Box>

                {/* Passo 2 - Credenciais */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            backgroundColor: '#1976d2', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, fontWeight: 'bold',
                        }}>2</Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Faça login com suas credenciais
                        </Typography>
                    </Box>

                    <Box sx={{ pl: 4.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Use o mesmo e-mail e senha da sua conta administradora:
                        </Typography>

                        {/* Email */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            p: 1.5, mb: 1, borderRadius: 2,
                            backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
                        }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">E-mail</Typography>
                                <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                                    {email}
                                </Typography>
                            </Box>
                            <Tooltip title={copiado === 'email' ? 'Copiado!' : 'Copiar'}>
                                <IconButton size="small" onClick={() => copiar(email, 'email')}>
                                    {copiado === 'email' ? <MdCheckCircle style={{ color: '#4CAF50' }} /> : <MdContentCopy />}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Senha */}
                        {senha ? (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                p: 1.5, borderRadius: 2,
                                backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Senha</Typography>
                                    <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                                        {senhaVisivel ? senha : '••••••••••'}
                                    </Typography>
                                </Box>
                                <Tooltip title={senhaVisivel ? 'Ocultar' : 'Exibir'}>
                                    <IconButton size="small" onClick={() => setSenhaVisivel(!senhaVisivel)}>
                                        {senhaVisivel ? <MdVisibilityOff /> : <MdVisibility />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={copiado === 'senha' ? 'Copiado!' : 'Copiar'}>
                                    <IconButton size="small" onClick={() => copiar(senha, 'senha')}>
                                        {copiado === 'senha' ? <MdCheckCircle style={{ color: '#4CAF50' }} /> : <MdContentCopy />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : (
                            <Box sx={{
                                p: 1.5, borderRadius: 2,
                                backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
                            }}>
                                <Typography variant="caption" color="text.secondary">Senha</Typography>
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    A mesma senha da sua conta no LocaSmartPro
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Passo 3 - Usar */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            backgroundColor: '#1976d2', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, fontWeight: 'bold',
                        }}>3</Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Realize vistorias pelo app
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                        Crie vistorias com fotos, checklists e assinaturas. Os relatórios ficam disponíveis aqui no LocaSmartPro automaticamente.
                    </Typography>
                </Box>

                {/* Botões */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => window.open('https://youtu.be/eY0JV3bmnMw', '_blank')}
                        sx={{
                            flex: 1,
                            py: 1.5,
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            borderRadius: 2,
                            borderColor: '#1976d2',
                            color: '#1976d2',
                        }}
                    >
                        Ver Demonstração
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={onClose}
                        sx={{
                            flex: 1,
                            py: 1.5,
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        }}
                    >
                        Entendi!
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default ModalOnboarding;
